import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, DownloadSimple, WifiSlash, X } from "@phosphor-icons/react";
import { registerServiceWorker, applyUpdate, clockBusy } from "@/lib/pwa";

const INSTALL_DISMISSED_KEY = "an_install_dismissed";

/** Offline notice + "new version available" prompt + subtle install button. */
export function PwaBanners() {
    const [updateReady, setUpdateReady] = useState(false);
    const [offline, setOffline] = useState(!navigator.onLine);
    const [installEvent, setInstallEvent] = useState(null);
    const [installDismissed, setInstallDismissed] = useState(
        () => localStorage.getItem(INSTALL_DISMISSED_KEY) === "1"
    );

    useEffect(() => {
        registerServiceWorker({ onUpdateReady: () => setUpdateReady(true) });

        const online = () => setOffline(false);
        const goneOffline = () => setOffline(true);
        window.addEventListener("online", online);
        window.addEventListener("offline", goneOffline);

        const onInstallPrompt = (e) => {
            e.preventDefault();
            setInstallEvent(e);
        };
        window.addEventListener("beforeinstallprompt", onInstallPrompt);
        const onInstalled = () => setInstallEvent(null);
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("online", online);
            window.removeEventListener("offline", goneOffline);
            window.removeEventListener("beforeinstallprompt", onInstallPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = async () => {
        if (!installEvent) return;
        installEvent.prompt();
        await installEvent.userChoice?.catch(() => null);
        setInstallEvent(null);
    };

    const dismissInstall = () => {
        localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
        setInstallDismissed(true);
    };

    return (
        <>
            {offline && (
                <div
                    className="fixed top-0 left-0 right-0 z-[60] bg-[#E6002C] text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-2"
                    style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
                    data-testid="offline-banner"
                >
                    <WifiSlash size={16} weight="fill" />
                    You're offline — live attendance data is unavailable until you reconnect.
                </div>
            )}

            {updateReady && (
                <div
                    className="fixed bottom-0 left-0 right-0 z-[60] bg-[#003366] text-white px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3"
                    style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
                    data-testid="update-banner"
                >
                    <span className="text-sm text-center">
                        A new version of Advance Networks is available. Refresh to update.
                    </span>
                    <Button
                        onClick={() => {
                            if (clockBusy.value) return;
                            applyUpdate();
                        }}
                        className="h-9 bg-white text-[#003366] hover:bg-slate-100 font-semibold"
                        data-testid="update-refresh-button"
                    >
                        <ArrowsClockwise size={15} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            )}

            {installEvent && !installDismissed && !offline && (
                <div
                    className="fixed bottom-4 right-4 z-[55] flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white/95 backdrop-blur px-2 py-1.5 shadow-lg"
                    style={{ marginBottom: "env(safe-area-inset-bottom)" }}
                    data-testid="install-prompt"
                >
                    <Button
                        onClick={install}
                        className="h-9 rounded-full bg-[#0066D9] hover:bg-[#0052ad] text-white text-sm"
                        data-testid="install-app-button"
                    >
                        <DownloadSimple size={15} className="mr-1.5" /> Install Advance Networks
                    </Button>
                    <button
                        onClick={dismissInstall}
                        className="h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600"
                        aria-label="Dismiss install prompt"
                        data-testid="install-dismiss-button"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </>
    );
}
